import {
  isAllowedReturnUrl,
  PLAY_PATHS,
  PROVIDER_PARAM,
  RETURN_TO_PARAM,
  resolveReturnUrl,
} from '@words-happen/app-urls';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { type IdentityRoute, ROUTES } from '../routes';
import { type BrowserNavigation, goBack, windowNavigation } from './browser-navigation';

/** Where players go when return_to is missing or not allowed: the game app's home. */
export const GAME_HOME_URL = new URL(PLAY_PATHS.home, PLAY_URL).href;

/**
 * Where the back chevron goes when there is no history to go back to: the game app's landing screen, never return_to,
 * which may be a page that needs sign-in and would send the player straight back here.
 */
export const GAME_WELCOME_URL = new URL(PLAY_PATHS.welcome, PLAY_URL).href;

const CARRIED_PARAMS = [PROVIDER_PARAM, RETURN_TO_PARAM];

/** The query string one identity route passes on to the next: return_to and provider, as received. */
export const carriedSearch = (params: URLSearchParams): string => {
  const carried = new URLSearchParams();
  for (const name of CARRIED_PARAMS) {
    const value = params.get(name);
    if (value !== null) {
      carried.set(name, value);
    }
  }
  const search = carried.toString();
  return search ? `?${search}` : '';
};

/** An identity route on `identityOrigin`, with `returnTo` when there is one: where an email's link continues to. */
export const identityContinueUrl = (
  path: IdentityRoute,
  identityOrigin: string,
  returnTo: string | undefined,
): string => {
  const url = new URL(path, identityOrigin);
  if (returnTo) {
    url.searchParams.set(RETURN_TO_PARAM, returnTo);
  }
  return url.href;
};

/**
 * The identity app's /verify-email, with `returnTo` when there is one: where a verification email's link continues
 * to, so a player who verifies in the same browser carries straight on into the game.
 */
export const verifyEmailContinueUrl = (identityOrigin: string, returnTo: string | undefined): string =>
  identityContinueUrl(ROUTES.verifyEmail, identityOrigin, returnTo);

/** What one identity route hands the next in history state, never the URL. */
export interface AuthRouteState {
  /** The address the player just entered or registered with, so the next screen can show it or prefill it. */
  email?: string;
  /**
   * Set on an automatic redirect between /sign-in and /verify-email, so the screen it lands on never redirects
   * straight back: at most one automatic redirect, never a loop.
   */
  redirected?: boolean;
  /** False when registration couldn't send the verification email, so /verify-email offers to resend it. */
  verificationSent?: boolean;
  /**
   * Set when a password reset succeeded but signing in with the new password didn't: /sign-in says the password has
   * changed and never signs in silently.
   */
  passwordChanged?: boolean;
}

/** A route's history state, keeping only well-formed fields. */
export const routeState = (state: unknown): AuthRouteState => {
  const { email, redirected, verificationSent, passwordChanged } = (state ?? {}) as Record<string, unknown>;
  return {
    ...(typeof email === 'string' && email ? { email } : {}),
    ...(redirected === true ? { redirected } : {}),
    ...(typeof verificationSent === 'boolean' ? { verificationSent } : {}),
    ...(passwordChanged === true ? { passwordChanged } : {}),
  };
};

/** The email from a route's history state, when it holds one. */
export const routeEmail = (state: unknown): string | undefined => routeState(state).email;

export interface AuthNavigation {
  /** return_to as received, unvalidated: what the session exchange hands the API to validate. */
  returnTo: string | null;
  /** The validated return_to, or GAME_HOME_URL. */
  returnUrl: string;
  /** Where verification emails continue to: /verify-email on IDENTITY_URL, keeping a valid return_to. */
  continueUrl: string;
  /** Where an email's link continues to: `path` on IDENTITY_URL, keeping a valid return_to. */
  continueUrlTo: (path: IdentityRoute) => string;
  /** Goes to another identity route, keeping return_to and provider, with `state` in history (not the URL). */
  switchTo: (path: IdentityRoute, state?: AuthRouteState, options?: { replace?: boolean }) => void;
  /** The back chevron: back through history (see goBack), otherwise GAME_WELCOME_URL. */
  back: () => void;
  /** Once the player has a session: a full page navigation to `returnUrl` (from the API), otherwise GAME_HOME_URL. */
  leave: (returnUrl: string | null) => void;
}

/** Navigation for the identity screens, from the current route's return_to. */
export const useAuthNavigation = (navigation: BrowserNavigation = windowNavigation): AuthNavigation => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const returnTo = params.get(RETURN_TO_PARAM);
  const returnUrl = resolveReturnUrl(returnTo, ALLOWED_RETURN_ORIGINS, GAME_HOME_URL);
  const allowed = isAllowedReturnUrl(returnTo, ALLOWED_RETURN_ORIGINS);
  const continueUrlTo = (path: IdentityRoute) =>
    identityContinueUrl(path, IDENTITY_URL, allowed ? returnUrl : undefined);

  return {
    returnTo,
    returnUrl,
    continueUrl: continueUrlTo(ROUTES.verifyEmail),
    continueUrlTo,
    switchTo: (pathname, state, options) =>
      navigate({ pathname, search: carriedSearch(params) }, { state, replace: options?.replace }),
    back: () => goBack(navigation, ALLOWED_RETURN_ORIGINS, GAME_WELCOME_URL),
    leave: (url) => navigation.assign(url || GAME_HOME_URL),
  };
};
